/**
 * Polling scheduler (guest projection every 5 s, notifications every 30 s, comments). Runs never
 * overlap; a trigger during a run (window focus, after a mutation) schedules exactly one follow-up
 * run; failures back off exponentially up to `maxDelayMs`. Timers are injectable for tests.
 */
export interface PollerOptions {
  run(): Promise<unknown>;
  intervalMs: number;
  maxDelayMs?: number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

export function nextDelay(intervalMs: number, failures: number, maxDelayMs: number): number {
  if (failures <= 0) return intervalMs;
  return Math.min(maxDelayMs, intervalMs * 2 ** Math.min(failures, 20));
}

export class Poller {
  private timer: unknown = null;
  private running = false;
  private pending = false;
  private active = false;
  private failures = 0;
  private readonly setT: (fn: () => void, ms: number) => unknown;
  private readonly clearT: (h: unknown) => void;
  private readonly maxDelay: number;

  constructor(private readonly opts: PollerOptions) {
    this.setT = opts.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
    this.clearT =
      opts.clearTimeout ?? ((h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>));
    this.maxDelay = opts.maxDelayMs ?? Math.max(opts.intervalMs, 5 * 60_000);
  }

  get isActive(): boolean {
    return this.active;
  }

  get failureCount(): number {
    return this.failures;
  }

  /** Starts polling with an immediate run. Idempotent. */
  start(): Promise<void> {
    if (this.active) return Promise.resolve();
    this.active = true;
    return this.tick();
  }

  /** Run as soon as possible (focus, after a write). */
  trigger(): Promise<void> {
    if (!this.active) return Promise.resolve();
    if (this.running) {
      this.pending = true;
      return Promise.resolve();
    }
    return this.tick();
  }

  stop(): void {
    this.active = false;
    this.pending = false;
    this.cancel();
  }

  private cancel(): void {
    if (this.timer !== null) this.clearT(this.timer);
    this.timer = null;
  }

  private async tick(): Promise<void> {
    this.cancel();
    this.running = true;
    try {
      await this.opts.run();
      this.failures = 0;
    } catch {
      this.failures += 1;
    } finally {
      this.running = false;
    }
    if (!this.active) return;
    if (this.pending) {
      this.pending = false;
      return this.tick();
    }
    this.timer = this.setT(
      () => {
        this.timer = null;
        void this.tick();
      },
      nextDelay(this.opts.intervalMs, this.failures, this.maxDelay),
    );
  }
}
